import { faPlayCircle } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ClauseListTOU from 'src/components/TradeRule/TimeOfUse/ClauseListTOU';
import { APIConfig } from 'src/config';
import {
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL, TRADE_RULE_STATE_PROPOSED,
} from 'src/util/constants';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import { buildTradePointInlineLabel } from 'src/util/i18n/tradePoint';

import TradeRuleTimelineStep from './TradeRuleTimelineStep';
import buildClauseListTOUIntro from './helpers/tradeRule';
import { getTradeData } from '../helpers';

/**
 * Returns the buy proposal details
 * @param {import('react-intl').IntlShape} intl - The intl object for internationalization.
 * @param {React.ReactElement} tradePointLabel
 * @param {object} counterParty
 * @returns {React.ReactElement} - buy proposal details
 */
const buyTradeDetails = (intl, tradePointLabel, counterParty) => {
  if (counterParty) {
    const counterPartyUser = displayUserName(intl, counterParty);
    return (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_buy_with_counterparty.details"
        defaultMessage="Buying for {tradePointLabel} from {counterPartyUser}"
        values={{
          tradePointLabel,
          counterPartyUser,
        }}
      />
    );
  }
  return (
    <FormattedMessage
      id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_buy_no_counterparty.details"
      defaultMessage="Buying for {tradePointLabel}"
      values={{
        tradePointLabel,
      }}
    />
  );
};

/**
 * Returns the sell proposal details
 * @param {import('react-intl').IntlShape} intl - The intl object for internationalization.
 * @param {React.ReactElement} tradePointLabel
 * @param {string} counterParty
 * @returns {React.ReactElement} - sell proposal details
 */
const sellTradeDetails = (intl, tradePointLabel, counterParty) => {
  if (counterParty) {
    const counterPartyUser = displayUserName(intl, counterParty);
    return (
      <FormattedMessage
        id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_sell_with_counterparty.details"
        defaultMessage="Selling from {tradePointLabel} to {counterPartyUser}"
        values={{
          tradePointLabel,
          counterPartyUser,
        }}
      />
    );
  }
  return (
    <FormattedMessage
      id="trade_rule.trade_rule_timeline.trade_rule_timeline_step_proposed.trade_direction_sell_no_counterparty.details"
      defaultMessage="Selling from {tradePointLabel}"
      values={{
        tradePointLabel,
      }}
    />
  );
};
/**
 * Description
 * @param {any} props
 * @returns {any} - TradeRuleTimelineStepProposed component
 */
function TradeRuleTimelineStepProposed(props) {
  const intl = useIntl();

  const {
    buyer, clauses, hasNextStep, isCurrentStep, ruleId, seller,
    timestamp, timezone, tradeType, user,
  } = props;

  const {
    direction, trader, counterParty,
  } = getTradeData(buyer, seller, user?.uuid) || {};

  const { user: counterPartyUser } = counterParty || {};

  const mode = APIConfig().MODE;

  // Memoized clause sorting with stable sort and null handling
  const clauseNodes = useMemo(() => {
    if (!clauses || !clauses.edges) return [];

    return clauses.edges
      .map((el) => el.node)
      .filter((node) => node && node.price !== null && node.price !== undefined)
      .sort((a, b) => {
        // Stable sort with secondary key (uuid) for deterministic ordering
        const priceDiff = b.price - a.price;
        if (priceDiff !== 0) return priceDiff;
        if (!a.uuid || !b.uuid) return 0;
        return a.uuid.localeCompare(b.uuid);
      });
  }, [clauses]);

  const tradePointLabel = buildTradePointInlineLabel(intl, trader?.tradePoint);

  return (
    <TradeRuleTimelineStep
      hasNextStep={hasNextStep}
      icon={<FontAwesomeIcon icon={faPlayCircle} />}
      isCurrentStep={isCurrentStep}
      ruleId={ruleId}
      state={TRADE_RULE_STATE_PROPOSED}
      tradeType={tradeType}
      timestamp={timestamp}
      timezone={timezone}
      user={user}
    >
      {tradePointLabel && (
        <li className="mb-3">
          {direction === TRADE_DIRECTION_BUY && (
            buyTradeDetails(intl, tradePointLabel, counterPartyUser)
          )}
          {direction === TRADE_DIRECTION_SELL && (
            sellTradeDetails(intl, tradePointLabel, counterPartyUser)
          )}

        </li>
      )}
      <li>
        {direction === TRADE_DIRECTION_BUY && (
          buildClauseListTOUIntro({
            intl,
            direction: TRADE_DIRECTION_BUY,
            mode,
          })
        )}
        {direction === TRADE_DIRECTION_SELL && (
          buildClauseListTOUIntro({
            intl,
            direction: TRADE_DIRECTION_SELL,
            mode,
          })
        )}
      </li>
      <ClauseListTOU clauseNodes={clauseNodes} />
    </TradeRuleTimelineStep>
  );
}

TradeRuleTimelineStepProposed.propTypes = {
  buyer: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  clauses: PropTypes.shape({
    edges: PropTypes.arrayOf(PropTypes.shape({
      node: (PropTypes.shape({
        ignoreDaylightSavings: PropTypes.bool.isRequired,
        ignorePublicHolidays: PropTypes.bool.isRequired,
        price: PropTypes.number.isRequired,
        timezone: PropTypes.string.isRequired,
      })),
    })),
  }).isRequired,
  isCurrentStep: PropTypes.bool.isRequired,
  hasNextStep: PropTypes.bool.isRequired,
  ruleId: PropTypes.string.isRequired,
  seller: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  timestamp: PropTypes.number.isRequired,
  timezone: PropTypes.string.isRequired,
  tradeType: PropTypes.string.isRequired,
  user: PropTypes.shape({
    uuid: PropTypes.string,
    email: PropTypes.string,
    givenName: PropTypes.string,
    familyName: PropTypes.string,
  }).isRequired,
};

export default TradeRuleTimelineStepProposed;
